import { useCallback, useEffect, useRef, useState } from "react";

const DEBOUNCE_MS = 500;

// The endpoint's response body is not documented, so accept the common "taken" shapes.
const isTaken = (res, param) => {
  if (res?.statusCode === 409) return true;
  if (!res?.success) return false;
  const d = res.resData;
  const node = d && typeof d === "object" && d[param] && typeof d[param] === "object" ? d[param] : d;
  if (typeof node === "boolean") return node === false;
  return (
    node?.available === false ||
    node?.is_available === false ||
    node?.exists === true ||
    node?.is_taken === true
  );
};

/**
 * Debounced onChange availability check.
 * service.checkAvailability({ [param]: value }) is called per field.
 * check(field, param, value, { valid, skip }) clears the state if the value is empty,
 * not yet in a valid format, or equal to the record being edited (skip).
 */
export default function useAvailabilityCheck(service) {
  const [taken, setTaken] = useState({});
  const [checking, setChecking] = useState({});
  const timers = useRef({});
  const seq = useRef({});

  useEffect(() => () => Object.values(timers.current).forEach(clearTimeout), []);

  const check = useCallback((field, param, value, { valid = true, skip = false, message = "Already registered" } = {}) => {
    clearTimeout(timers.current[field]);
    const id = (seq.current[field] = (seq.current[field] || 0) + 1);
    setTaken((t) => (t[field] ? { ...t, [field]: "" } : t));

    if (!value || !valid || skip) {
      setChecking((c) => ({ ...c, [field]: false }));
      return;
    }
    setChecking((c) => ({ ...c, [field]: true }));
    timers.current[field] = setTimeout(async () => {
      const res = await service.checkAvailability({ [param]: value });
      if (seq.current[field] !== id) return; // a newer keystroke superseded this call
      setChecking((c) => ({ ...c, [field]: false }));
      if (isTaken(res, param)) {
        setTaken((t) => ({ ...t, [field]: message }));
      }
    }, DEBOUNCE_MS);
  }, [service]);

  const hasTaken = Object.values(taken).some(Boolean);
  const isChecking = Object.values(checking).some(Boolean);

  return { taken, checking, check, hasTaken, isChecking };
}
